/**
 * Client-side JavaScript for the redesigned report shell: the verdict band,
 * the needs-attention block, and the detail section rail.
 *
 * These replace `renderSummary` and add two new surfaces; the test grid and
 * the drawer keep their existing renderers and are only enriched in place.
 *
 * ES5-compatible vanilla JS, concatenated into the report's single inline
 * script under a CSP that permits no external resources.
 */

export const JS_LAYOUT = `
  /* ── Verdict band ── */

  /* Both report modes hand us a summary; streaming's carries fewer fields. */
  function vNum(v){return typeof v==='number'?v:0;}

  function renderVerdictBar(s){
    var order=[
      {k:'passed',n:vNum(s.passed)},{k:'failed',n:vNum(s.failed)},
      {k:'flaky',n:vNum(s.flaky)},{k:'timedout',n:vNum(s.timedout)},
      {k:'skipped',n:vNum(s.skipped)}
    ];
    var total=0;for(var i=0;i<order.length;i++)total+=order[i].n;
    if(total===0)return '<div class="verdict-bar"><span class="verdict-bar-empty"></span></div>';
    var h='<div class="verdict-bar">';
    for(var j=0;j<order.length;j++){
      if(order[j].n===0)continue;
      h+='<span class="vb-'+order[j].k+'" style="flex:'+order[j].n+'"></span>';
    }
    return h+'</div>';
  }

  function renderVerdictHeadline(s){
    var failed=vNum(s.failed),total=vNum(s.total),flaky=vNum(s.flaky);
    if(failed>0)return failed+' of '+total+' test'+(total===1?'':'s')+' failed';
    if(flaky>0)return flaky+' of '+total+' test'+(total===1?'':'s')+' passed on retry';
    if(total===0)return 'No tests ran';
    return 'All '+total+' test'+(total===1?'':'s')+' passed';
  }

  /* Where the failures are, when they are all in one place — saves a scan. */
  function failureLocation(){
    var files={};var n=0;
    for(var i=0;i<tests.length;i++){
      var t=tests[i];
      if(t.status!=='failed'&&t.status!=='timedout')continue;
      if(!files[t.filePath]){files[t.filePath]=1;n++;}
    }
    if(n!==1)return null;
    for(var f in files){if(files.hasOwnProperty(f))return f;}
    return null;
  }

  function renderSummary(){
    var s=data.summary||{};
    var failed=vNum(s.failed),flaky=vNum(s.flaky);
    var cls=failed>0?'v-failed':(flaky>0?'':'v-passed');

    var h='<div class="verdict"><div class="verdict-main '+cls+'">';

    h+='<div class="verdict-head"><div class="verdict-title">'+esc(renderVerdictHeadline(s))+'</div>';
    var where=failed>0?failureLocation():null;
    if(where)h+='<div class="verdict-where">all in <code>'+esc(where.split('/').pop())+'</code></div>';
    h+='<div class="verdict-dur"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>'+fmtDur(data.totalDuration||0)+'</div>';
    h+='</div>';

    h+=renderVerdictBar(s);

    /* Non-zero statuses get a real count; the rest collapse to one grey line
       rather than each taking a sixth of the width. */
    var kinds=[
      {k:'passed',l:'passed',c:'#22c55e'},{k:'failed',l:'failed',c:'#ef4444'},
      {k:'flaky',l:'flaky',c:'#f59e0b'},{k:'timedout',l:'timed out',c:'#f97316'},
      {k:'skipped',l:'skipped',c:'#6b7280'}
    ];
    var counts='',zeros=[];
    for(var m=0;m<kinds.length;m++){
      var kk=kinds[m],n=vNum(s[kk.k]);
      if(n>0){
        counts+='<div class="vc"><span class="vc-dot" style="background:'+kk.c+'"></span><span class="vc-n">'+n+'</span><span class="vc-l">'+kk.l+'</span></div>';
      }else{
        zeros.push('0 '+kk.l);
      }
    }
    h+='<div class="verdict-counts">'+counts;
    if(zeros.length>0)h+='<div class="verdict-zeros">'+esc(zeros.join(' \\u00b7 '))+'</div>';
    h+='</div>';

    h+='</div>';

    /* What this run actually captured — only tiles with something in them. */
    var tiles=[];
    if(vNum(s.visualChecks)>0)tiles.push({n:vNum(s.visualChecks),l:'visual checks',cls:''});
    if(vNum(s.visualDomChanges)>0)tiles.push({n:vNum(s.visualDomChanges),l:'DOM changes',cls:' vs-dom'});
    if(vNum(s.totalActionSteps)>0)tiles.push({n:vNum(s.totalActionSteps),l:'actions',cls:''});
    if(vNum(s.totalApiCalls)>0)tiles.push({n:vNum(s.totalApiCalls),l:'API calls',cls:''});
    if(vNum(s.totalNavigations)>0)tiles.push({n:vNum(s.totalNavigations),l:'navigations',cls:''});

    if(tiles.length>0){
      h+='<div class="verdict-side"><div class="verdict-side-label">Captured this run</div><div class="verdict-side-grid">';
      for(var q=0;q<Math.min(tiles.length,4);q++){
        h+='<div><div class="vs-n'+tiles[q].cls+'">'+tiles[q].n.toLocaleString()+'</div><div class="vs-l">'+esc(tiles[q].l)+'</div></div>';
      }
      h+='</div></div>';
    }

    h+='</div>';
    summaryEl.innerHTML=h;
  }

  /* ── Needs attention ── */

  /* The first line of the error, which is the part that says what happened.
     Playwright's colour codes are stripped by the reporter before this. */
  function causeLine(t){
    if(t.firstFailedAssertion&&typeof assertionSentence==='function'){
      return '<em>'+assertionSentence(t.firstFailedAssertion)+'</em>';
    }
    if(t.assertionsFailed>0&&typeof apiFailureSentence==='function'&&t.apiAssertions){
      var sentence=apiFailureSentence(t.apiAssertions);
      if(sentence)return '<em>'+sentence+'</em>';
    }
    if(evVisualFailed(t)>0&&t.errorMessage){
      var px=t.errorMessage.match(/([\\d,]+) pixels \\(ratio ([\\d.]+)/);
      if(px)return '<em>'+esc(px[1])+' px differ</em>';
    }
    if(!t.errorMessage)return '';
    var first=String(t.errorMessage).split('\\n')[0].trim();
    if(first.length>150)first=first.slice(0,150)+'\\u2026';
    return '<em>'+esc(first)+'</em>';
  }

  function evidenceChips(t){
    var h='';
    /* These accessors read both report shapes — see html-js-render. */
    var _vis=evVisualCount(t),_visFail=evVisualFailed(t),_dom=evDomChanges(t),_net=evNetworkCount(t);
    if(_vis>0){
      h+='<span class="nchip'+(_visFail>0?' nchip-visual':'')+'"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 3v18"/></svg>'+(_visFail>0?'Visual diff':'Visual')+'</span>';
    }
    if(_dom>0){
      h+='<span class="nchip nchip-dom"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 3H5a2 2 0 00-2 2v3M16 3h3a2 2 0 012 2v3M8 21H5a2 2 0 01-2-2v-3M16 21h3a2 2 0 002-2v-3"/></svg>'+_dom+' element'+(_dom===1?'':'s')+'</span>';
    }
    if(evHasArtifacts(t)){
      h+='<span class="nchip"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="5 3 19 12 5 21"/></svg>Video</span>';
    }
    if(_net>0){
      h+='<span class="nchip"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 12h4l3 8 4-16 3 8h4"/></svg>'+_net+' requests</span>';
    }
    if(t.apiCallCount>0){
      h+='<span class="nchip">'+t.apiCallCount+' API call'+(t.apiCallCount===1?'':'s')+'</span>';
    }
    if(t.assertionsFailed>0){
      h+='<span class="nchip nchip-visual">'+t.assertionsFailed+' failed assertion'+(t.assertionsFailed===1?'':'s')+'</span>';
    }else if(t.assertionsPassed>0){
      h+='<span class="nchip">'+t.assertionsPassed+' assertion'+(t.assertionsPassed===1?'':'s')+'</span>';
    }
    if(typeof mobileEvidenceChips==='function')h+=mobileEvidenceChips(t);
    return h;
  }

  function renderNeedsAttention(){
    var el=document.getElementById('needs-attention');
    if(!el)return;

    var bad=[];
    for(var i=0;i<tests.length;i++){
      var st=tests[i].status;
      if(st==='failed'||st==='timedout'||st==='flaky')bad.push(tests[i]);
    }
    if(bad.length===0){el.innerHTML='';return;}

    /* Hard failures first, then flakes; biggest problems at the top. */
    bad.sort(function(a,b){
      var rank=function(x){return x.status==='flaky'?1:0;};
      return rank(a)-rank(b);
    });

    var h='<div class="needs"><div class="needs-head"><span class="needs-title">Needs attention</span><span class="needs-count">'+bad.length+'</span></div><div class="needs-list">';

    for(var j=0;j<Math.min(bad.length,8);j++){
      var t=bad[j];
      h+='<div class="needs-card'+(t.status==='flaky'?' nc-flaky':'')+'" data-testid="'+esc(t.testId)+'">';

      h+='<div class="needs-body">';
      h+='<div class="needs-row1"><span class="needs-name">'+esc(t.title)+'</span>';
      if(t.testType&&t.testType!=='unknown')h+='<span class="trb trb-type">'+esc(t.testType)+'</span>';
      if(t.status==='flaky')h+='<span class="trb trb-flaky">passed on retry</span>';
      h+='<span class="needs-file">'+esc(t.filePath)+'</span></div>';

      var cause=causeLine(t);
      if(cause)h+='<div class="needs-cause">'+cause+'</div>';

      var chips=evidenceChips(t);
      if(chips)h+='<div class="needs-chips">'+chips+'</div>';
      h+='</div>';

      /* Show the failure, do not only describe it. */
      var _diff=evDiffPath(t);
      if(_diff){
        h+='<div class="needs-thumb"><img src="'+esc(_diff)+'" alt="Pixel diff"><div class="needs-thumb-cap">pixel diff</div></div>';
      }

      h+='<div class="needs-right"><div class="needs-dur">'+fmtDur(t.duration)+'</div>';
      h+='<div class="needs-open">Open<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M9 18l6-6-6-6"/></svg></div></div>';

      h+='</div>';
    }

    if(bad.length>8)h+='<div class="needs-thumb-cap" style="text-align:left;padding:4px 2px">and '+(bad.length-8)+' more below</div>';

    el.innerHTML=h+'</div></div>';
  }

  /* ── Detail section rail ── */

  var RAIL_SECTIONS=[
    {id:'failure',label:'Why it failed',fail:true},
    {id:'visual',label:'Visual'},
    {id:'dom',label:'Elements',dom:true},
    {id:'assertions',label:'Assertions'},
    {id:'artifacts',label:'Video & trace'},
    {id:'actions',label:'Actions'},
    {id:'timeline',label:'Timeline'},
    {id:'api',label:'API calls'},
    {id:'console',label:'Console'},
    {id:'network',label:'Network'}
  ];

  function railIcon(id){
    if(id==='failure')return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M12 8v4M12 16h.01"/></svg>';
    if(id==='visual')return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 3v18"/></svg>';
    if(id==='dom')return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 3H5a2 2 0 00-2 2v3M16 3h3a2 2 0 012 2v3M8 21H5a2 2 0 01-2-2v-3M16 21h3a2 2 0 002-2v-3"/></svg>';
    if(id==='artifacts')return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="5 3 19 12 5 21"/></svg>';
    if(id==='actions')return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 20V10M18 20V4M6 20v-4"/></svg>';
    if(id==='assertions')return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6L9 17l-5-5"/></svg>';
    if(id==='console')return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 17l6-6-6-6M12 19h8"/></svg>';
    return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 12h4l3 8 4-16 3 8h4"/></svg>';
  }

  /* Built from what the drawer actually rendered, so the rail can never
     offer a jump to a section that is not there. */
  function renderDrawerRail(present,t){
    var h='<div class="rail-group"><div class="rail-label">Evidence</div>';
    var first=true;
    for(var i=0;i<RAIL_SECTIONS.length;i++){
      var sec=RAIL_SECTIONS[i];
      var info=present[sec.id];
      if(!info)continue;
      h+='<button class="rail-item'+(sec.fail?' rail-fail':'')+(first?' active':'')+'" data-rail="'+sec.id+'">';
      h+=railIcon(sec.id);
      h+='<span class="rail-name">'+esc(sec.label)+'</span>';
      if(info.n)h+='<span class="rail-n'+(sec.dom?' rail-n-dom':'')+'">'+info.n+'</span>';
      h+='</button>';
      first=false;
    }
    h+='</div>';

    h+='<div class="rail-meta"><div class="rail-label">Test</div><div class="rail-meta-rows">';
    if(t.testId)h+='<div class="rail-meta-row"><span class="rail-meta-k">ID</span><span class="rail-meta-v mono">'+esc(String(t.testId).substring(0,12))+'</span></div>';
    if(t.suiteName)h+='<div class="rail-meta-row"><span class="rail-meta-k">Suite</span><span class="rail-meta-v">'+esc(t.suiteName)+'</span></div>';
    h+='<div class="rail-meta-row"><span class="rail-meta-k">Retry</span><span class="rail-meta-v">'+(t.retryCount>0?t.retryCount+'':'none')+'</span></div>';
    h+='</div></div>';
    return h;
  }

  /* Scroll the page so a section's top is just under the viewport top.
     A smooth scrollIntoView was silently dropped here: the rail
     item is a <button>, and a smooth scroll requested from inside its click
     handler never started — the section stayed off-screen and the rail entry
     did nothing. Instant scrolling from a computed offset always lands, and
     smooth is attempted first with a check that it actually moved. */
  function jumpToSection(el){
    if(!el)return;
    var pageY=window.scrollY!==undefined?window.scrollY:(window.pageYOffset||0);
    var y=Math.max(0,el.getBoundingClientRect().top+pageY-12);
    try{
      window.scrollTo({top:y,behavior:'smooth'});
    }catch(e){
      window.scrollTo(0,y);
    }
    setTimeout(function(){
      var now=window.scrollY!==undefined?window.scrollY:(window.pageYOffset||0);
      if(Math.abs(now-y)>40)window.scrollTo(0,y);
    },350);
  }

  /* Jump, rather than scroll past everything above it. */
  document.addEventListener('click',function(e){
    var btn=e.target.closest?e.target.closest('.rail-item'):null;
    if(!btn)return;
    var id=btn.getAttribute('data-rail');

    /* Elements is not a section of its own — it is a pane inside Visual. Jump
       to Visual and switch the pane, so the rail entry lands where it says. */
    var sectionId=id==='dom'?'visual':id;
    var target=document.querySelector('[data-section="'+sectionId+'"]');
    if(id==='dom'&&target){
      var btns=target.querySelectorAll('.seg-btn');
      for(var b2=0;b2<btns.length;b2++){
        if(btns[b2].textContent==='Elements'){btns[b2].click();break;}
      }
    }
    jumpToSection(target);
    var all=document.querySelectorAll('.rail-item');
    for(var i=0;i<all.length;i++)all[i].classList.remove('active');
    btn.classList.add('active');
  });

  /* A failing test opens on its failure, not on the top of a long page. */
  document.addEventListener('click',function(e){
    var card=e.target.closest?e.target.closest('.needs-card'):null;
    if(!card)return;
    openTest(card.getAttribute('data-testid'));
  });
`;

/**
 * Routing for the detail view.
 *
 * The detail is a page, not an overlay. It gets its own history entry, so the
 * browser Back button works and a link to a failing test opens that test.
 *
 * Hash routing rather than `pushState`: the embedded report is a single file
 * people open straight off disk, and `pushState` is not permitted on `file://`.
 * A hash works in both that case and behind the report server.
 */
export const JS_ROUTER = `
  var overviewViewEl=document.getElementById('view-overview');
  var detailViewEl=document.getElementById('view-detail');
  var detailNavEl=document.getElementById('detail-nav');
  var _baseTitle=document.title;
  var _overviewScroll=0;
  var _currentTestId=null;
  /* Set while we are the ones changing the URL, so our own write does not
     bounce back through the router as if the user had pressed Back. */
  var _suppressRoute=false;

  function testHash(id){return '#test/'+encodeURIComponent(id);}

  function parseTestHash(){
    var h=String(location.hash||'');
    if(h.indexOf('#test/')!==0)return null;
    try{return decodeURIComponent(h.slice(6));}catch(e){return h.slice(6);}
  }

  /* Switch the view FIRST, then record it in the URL.
     Driving the switch off the hashchange event made the whole navigation
     hostage to an event that is not always delivered — the view simply never
     changed. The URL still updates, so deep links and Back keep working, but
     nothing user-visible depends on the event arriving. */
  function setHashQuietly(value){
    try{
      _suppressRoute=true;
      if(history&&history.replaceState){
        history.pushState(null,'',value||location.pathname+location.search);
      }else{
        location.hash=value||'';
      }
    }catch(e){
      /* pushState is refused on file:// — the hash still works there. */
      try{location.hash=value||'';}catch(e2){/* no URL update; the view is right */}
    }
    setTimeout(function(){_suppressRoute=false;},0);
  }

  /* Whether the detail view we are showing came from a push of ours, and so
     can be closed by unwinding history instead of pushing again. */
  var _pushedDetailEntry=false;

  function openTest(testId){
    if(!testId||!testMap[testId])return;
    showTest(testId);
    setHashQuietly(testHash(testId));
    _pushedDetailEntry=true;
  }

  function closeTest(){
    if(!_currentTestId)return;
    /* Leave the detail entry behind rather than stacking another one on top of
       it. setHashQuietly pushes, so closing this way left #test/<id> in
       history *behind* the overview — the browser Back button then replayed it
       and re-opened the very test the reader had just closed.
       history.back() unwinds our own push; routeFromHash handles the
       resulting popstate and switches the view. */
    if(_pushedDetailEntry&&history&&history.back){
      _pushedDetailEntry=false;
      history.back();
      return;
    }
    showOverview();
    setHashQuietly('');
  }

  /* Prev/next walk the CURRENTLY FILTERED list, so stepping through a
     "Failed" filter visits the failures and nothing else. */
  function testNeighbours(testId){
    var list=getFilteredTests();
    for(var i=0;i<list.length;i++){
      if(list[i].testId===testId)return {list:list,idx:i};
    }
    return {list:list,idx:-1};
  }

  function stepTest(delta){
    if(!_currentTestId)return;
    var n=testNeighbours(_currentTestId);
    if(n.idx<0)return;
    var next=n.list[n.idx+delta];
    if(next)openTest(next.testId);
  }

  function renderDetailNav(testId){
    var t=testMap[testId]||{};
    var n=testNeighbours(testId);
    var pos=n.idx>=0?(n.idx+1)+' of '+n.list.length:'';

    var h='<button class="detail-back" id="detail-back"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>Back to results</button>';
    if(t.filePath)h+='<span class="detail-crumb">'+esc(t.filePath)+'</span>';

    h+='<div class="detail-steps">';
    var noPrev=n.idx<=0;
    var noNext=n.idx<0||n.idx>=n.list.length-1;
    h+='<button class="detail-step'+(noPrev?' is-off':'')+'" id="detail-prev" title="Previous test"'+(noPrev?' disabled':'')+'><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg></button>';
    if(pos)h+='<span class="detail-pos">'+esc(pos)+'</span>';
    h+='<button class="detail-step'+(noNext?' is-off':'')+'" id="detail-next" title="Next test"'+(noNext?' disabled':'')+'><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg></button>';
    h+='</div>';

    detailNavEl.innerHTML=h;
  }

  function showOverview(){
    _currentTestId=null;
    var v=drawerBodyEl.querySelector('video');
    if(v)v.pause();
    detailViewEl.hidden=true;
    overviewViewEl.hidden=false;
    document.title=_baseTitle;
    /* Put the reader back where they were in the list, not at the top. */
    window.scrollTo(0,_overviewScroll);
  }

  function showTest(testId){
    if(_currentTestId===null)_overviewScroll=window.scrollY||window.pageYOffset||0;
    _currentTestId=testId;
    overviewViewEl.hidden=true;
    detailViewEl.hidden=false;
    window.scrollTo(0,0);
    var t=testMap[testId];
    document.title=(t&&t.title?t.title+' \u2014 ':'')+_baseTitle;
    renderDetailNav(testId);
    loadAndRenderTest(testId);
  }

  /* Back/forward, and the initial load of a pasted link. Idempotent: a
     re-entrant call for the view already showing does nothing. */
  function routeFromHash(){
    if(_suppressRoute)return;
    var id=parseTestHash();
    if(id&&testMap[id]){
      if(_currentTestId!==id)showTest(id);
    }else if(_currentTestId!==null){
      showOverview();
    }
  }

  window.addEventListener('hashchange',routeFromHash);
  window.addEventListener('popstate',routeFromHash);
`;
